/**
 * Editor State IndexedDB Storage
 *
 * Stores editor UI state (spec 018) locally, keyed by scene id, with the time
 * it was written. The server copy is written only inside a content save and
 * once on leaving the editor (sceneStore); nothing here talks to the server.
 */

import type { EditorState } from '../../../Shared/src/@types';

const DB_NAME = 'StoryMotionEditor';
const DB_VERSION = 1;
const STORE_NAME = 'editorState';

export interface EditorStateRecord {
  sceneId: number;
  timestamp: number;
  state: EditorState;
}

let dbInstance: IDBDatabase | null = null;

/**
 * Open/create the IndexedDB database
 */
function openDB(): Promise<IDBDatabase> {
  if (dbInstance) {
    return Promise.resolve(dbInstance);
  }
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      console.error('Failed to open editor state DB:', request.error);
      reject(request.error);
    };
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store for editor state, keyed by sceneId
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'sceneId' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Get editor state for a scene from IndexedDB
 */
export async function getLocalEditorState(sceneId: number): Promise<EditorStateRecord | null> {
  try {
    const db = await openDB();
    
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(sceneId);
      
      request.onerror = () => {
        console.error('Failed to get editor state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve(request.result || null);
      };
    });
  } catch (error) {
    console.error('Error accessing IndexedDB:', error);
    return null;
  }
}

/**
 * Save editor state to IndexedDB
 */
export async function saveLocalEditorState(sceneId: number, state: EditorState): Promise<void> {
  try {
    const db = await openDB();
    
    const record: EditorStateRecord = {
      sceneId,
      timestamp: Date.now(),
      state,
    };
    
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(record);
      
      request.onerror = () => {
        console.error('Failed to save editor state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve();
      };
    });
  } catch (error) {
    console.error('Error saving to IndexedDB:', error);
  }
}

/**
 * Delete editor state for a scene from IndexedDB
 */
export async function deleteLocalEditorState(sceneId: number): Promise<void> {
  try {
    const db = await openDB();
    
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(sceneId);
      
      request.onerror = () => {
        console.error('Failed to delete editor state:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve();
      };
    });
  } catch (error) {
    console.error('Error deleting from IndexedDB:', error);
  }
}

/**
 * Get all stored editor states (for debugging/cleanup)
 */
export async function getAllLocalEditorStates(): Promise<EditorStateRecord[]> {
  try {
    const db = await openDB();
    
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();
      
      request.onerror = () => {
        console.error('Failed to get all editor states:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        resolve(request.result || []);
      };
    });
  } catch (error) {
    console.error('Error accessing IndexedDB:', error);
    return [];
  }
}
